// TESSERAX — RÉSONANCE DE DECK (bonus de synergie in-run des Voies).
//
// Tenir 2 / 3 / 4 reliques d'une MÊME Voie (cf. core/voies) éveille des paliers de résonance :
// de petits bonus passifs data-driven qui rendent le « build » TANGIBLE pendant la run — on ne
// collectionne plus des reliques isolées, on ASSEMBLE un deck. Les paliers CUMULENT (×2 reste
// actif sous ×3), à la manière des « set bonuses ».
//
// GARDE-FOUS (équilibrage & anti-triche, cf. docs/balance-baseline.md) :
// · Modestie : chaque palier vaut moins que la relique la plus faible de sa voie — la résonance
//   RÉCOMPENSE l'engagement dans un archétype, elle ne remplace jamais une relique.
// · Tous les effets passent par la surface PLAFONNÉE de RelicApi : buffScore → RUN_SCORE_MULT_CAP,
//   setNextMult → NEXT_MULT_CAP (loi additive), buffMaxCharge → MAX_CHARGE_CAP, addCharge → budget
//   PARTAGÉ RELIC_CHARGE_PER_EVENT (les crochets de résonance passent APRÈS les reliques dans le
//   même emitRelic → jamais plus d'1 charge rendue par évènement, toutes sources confondues).
// · addMeter est bloqué pendant la Zone (moteur) → aucune résonance ne peut nourrir une perma-Zone.
// · AUCUN RNG (contrat RelicApi) ; les compteurs « tous les N » vivent dans une mémoire de palier
//   (relicMem['res_<voie>_<count>']) → déterministe, rejouable bit-exact.
// · L'appartenance à une voie est EXPLICITE (voieOf), jamais les tags bruts — sinon les reliques
//   qui portent `zone` incidemment (Prisme, Faille…) compteraient pour le Flux.
//
// Module PUR : ni THREE ni DOM. rules.ts (vif) et sim.ts (anti-triche) l'appliquent en MIROIR
// STRICT : inits au franchissement dans acquireRelic, crochets `on` en queue d'emitRelic.

import type { RelicApi, RelicEvent } from './relics';
import { VOIES, deckCount } from './voies';
import type { VoieId } from './voies';

export interface ResonanceTier {
  count: number;                                            // reliques de la voie requises (2|3|4)
  text: string;                                             // FR (i18n res.<voie>.<count>)
  init?: (api: RelicApi) => void;                           // appliqué UNE fois au franchissement
  on?: Partial<Record<RelicEvent, (api: RelicApi) => void>>;
}

export const RESONANCES: Record<VoieId, ResonanceTier[]> = {
  bascule: [
    { count: 2, text: 'Toutes les 3 Bascules, rembourse une charge.',
      on: { bascule: a => { a.self.n = (a.self.n || 0) + 1; if (a.self.n % 3 === 0) a.addCharge(1); } } },
    { count: 3, text: '+1 charge de Bascule maximum.', init: a => a.buffMaxCharge(1) },
    { count: 4, text: 'Une Bascule qui efface rapporte +80 × niveau.',
      on: { bascule: a => { if (a.ev && ((a.ev.rows || 0) + (a.ev.slabs || 0)) > 0) a.addScore(80 * a.level, 'Résonance ⟲'); } } },
  ],
  croix: [
    { count: 2, text: 'Chaque Croix rapporte +120 × Croix × niveau.',
      on: { clear: a => { if ((a.ev.cross || 0) > 0) a.addScore(a.ev.cross * 120 * a.level, 'Résonance ✛'); } } },
    { count: 3, text: 'Chaque Croix regonfle la Zone et la prolonge.',
      on: { clear: a => { if ((a.ev.cross || 0) > 0) { a.addMeter(6); a.extendZone(400); } } } },
    { count: 4, text: 'Chaque Croix rend une charge de Bascule.',
      on: { clear: a => { if ((a.ev.cross || 0) > 0) a.addCharge(1); } } },
  ],
  continuum: [
    { count: 2, text: 'Chaque clear de CONTINUUM rapporte +40 × continuum × niveau.',
      on: { continuum: a => a.addScore((a.ev.count || 0) * 40 * a.level, 'Résonance ⟁') } },
    { count: 3, text: 'Chaque clear de CONTINUUM regonfle la Zone selon sa profondeur.',
      on: { continuum: a => a.addMeter(3 + (a.ev.count || 0)) } },
    { count: 4, text: 'Un CONTINUUM profond (≥4) aiguise le prochain clair (+×0.2).',
      on: { continuum: a => { if ((a.ev.count || 0) >= 4) a.setNextMult(1.2); } } },
  ],
  pli: [
    { count: 2, text: 'Chaque TESSERAX rapporte +150 × niveau.',
      on: { tessera: a => a.addScore(150 * a.level, 'Résonance ⧉') } },
    { count: 3, text: 'Chaque TESSERAX regonfle la Zone (+8).',
      on: { tessera: a => a.addMeter(8) } },
    { count: 4, text: 'Chaque PLI joué aiguise le prochain clair (+×0.15).',
      on: { fold: a => a.setNextMult(1.15) } },
  ],
  flux: [
    { count: 2, text: 'La jauge de Zone se remplit 15 % plus vite.', init: a => a.buffMeter(1.15) },
    { count: 3, text: 'La sortie de Zone rapporte +30 par ligne banquée × niveau.',
      on: { zoneEnd: a => { const b = (a.ev && a.ev.bankLines) || 0; if (b > 0) a.addScore(b * 30 * a.level, 'Résonance ◉'); } } },
    { count: 4, text: 'La sortie de Zone aiguise le prochain clair (+×0.2).',
      on: { zoneEnd: a => a.setNextMult(1.2) } },
  ],
};

// Clé de la mémoire de palier dans relicMem — préfixe `res_` : aucune collision possible avec un
// id de relique (ids en minuscules sans le préfixe ; `resonance`/`resonateur` sont des ids EXACTS).
export function resonanceMemKey(voie: VoieId, count: number): string { return 'res_' + voie + '_' + count; }

// Palier le plus haut ATTEINT d'une voie pour un build (0 si < 2 reliques de la voie).
export function resonanceLevel(ownedIds: string[], voie: VoieId): number {
  const n = deckCount(ownedIds, voie);
  let best = 0;
  for (const t of RESONANCES[voie]) if (n >= t.count) best = t.count;
  return best;
}

// Paliers FRANCHIS entre deux états du build (exclusif avant, inclusif après) — pour appliquer
// leurs `init` UNE seule fois à l'acquisition. Ordre FIXE : VOIES puis paliers croissants
// (déterminisme du rejeu : l'ordre d'application ne dépend que du contenu du build).
export interface CrossedTier { voie: VoieId; tier: ResonanceTier; }
export function crossedTiers(beforeIds: string[], afterIds: string[]): CrossedTier[] {
  const out: CrossedTier[] = [];
  for (const v of VOIES) {
    const before = deckCount(beforeIds, v.id), after = deckCount(afterIds, v.id);
    if (after <= before) continue;
    for (const t of RESONANCES[v.id]) if (t.count > before && t.count <= after) out.push({ voie: v.id, tier: t });
  }
  return out;
}

// Crochets `on` ACTIFS pour un évènement donné, dans l'ordre FIXE de VOIES puis des paliers.
// Les paliers cumulent : tous ceux dont le seuil est atteint participent.
export function activeResonanceHooks(ownedIds: string[], event: RelicEvent): CrossedTier[] {
  const out: CrossedTier[] = [];
  for (const v of VOIES) {
    const n = deckCount(ownedIds, v.id);
    if (n < 2) continue;
    for (const t of RESONANCES[v.id]) if (n >= t.count && t.on && t.on[event]) out.push({ voie: v.id, tier: t });
  }
  return out;
}

// Résonances actives d'un build, pour l'AFFICHAGE (chips HUD, ligne du draft) — lecture seule.
export interface ActiveResonance { voie: VoieId; count: number; level: number; }
export function activeResonances(ownedIds: string[]): ActiveResonance[] {
  const out: ActiveResonance[] = [];
  for (const v of VOIES) {
    const level = resonanceLevel(ownedIds, v.id);
    if (level >= 2) out.push({ voie: v.id, count: deckCount(ownedIds, v.id), level });
  }
  return out;
}
